import React, { useMemo } from "react";
import { Box, IconButton, Slider, Tooltip, Typography } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useTranslation } from "react-i18next";
import { useSound } from "../../../../../../context/juno/SoundContext";
import { ColumnBox } from "../../../../../../utils/styledBox";

interface GuidanceScaleRange {
  min: number;
  max: number;
  step: number;
}

const GuidanceScaleControl: React.FC = () => {
  const { t } = useTranslation();
  const { guidanceScale, setGuidanceScale, aiEngine } = useSound();

  // 現在選択されているエンジンのguidanceScale値を取得
  const currentGuidanceScale = guidanceScale[aiEngine];

  // モデルごとのguidanceScaleの範囲を設定
  const guidanceScaleRange = useMemo<GuidanceScaleRange>(() => {
    switch (aiEngine) {
      case "MMAUDIO":
        return { min: 0.5, max: 20, step: 0.1 };
      case "ELEVENLABS":
        return { min: 0.1, max: 1, step: 0.1 };
      case "DIFFRHYTHM":
        return { min: 1, max: 10, step: 0.1 };
      default:
        return { min: 1, max: 20, step: 0.1 };
    }
  }, [aiEngine]);

  // ガイダンススケールの値が範囲外の場合、クランプする
  const clampedValue = Math.max(guidanceScaleRange.min, Math.min(currentGuidanceScale, guidanceScaleRange.max));

  // 表示用にフォーマット（小数点以下1桁）
  const displayValue = Number(clampedValue.toFixed(1));

  // モデルごとのマーカー
  const getMarks = () => {
    switch (aiEngine) {
      case "MMAUDIO":
        return [
          { value: 0.5, label: "0.5" },
          { value: 10, label: "10" },
          { value: 20, label: "20" },
        ];
      case "ELEVENLABS":
        return [
          { value: 0.1, label: "0.1" },
          { value: 0.5, label: "0.5" },
          { value: 1, label: "1" },
        ];
      case "DIFFRHYTHM":
        return [
          { value: 1, label: "1" },
          { value: 5, label: "5" },
          { value: 10, label: "10" },
        ];
      default:
        return [
          { value: 1, label: "1" },
          { value: 10, label: "10" },
          { value: 20, label: "20" },
        ];
    }
  };

  return (
    <ColumnBox>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="body2">
          {t("juno.sound.params.guidanceScale")}: {displayValue}
        </Typography>
        <Tooltip title={t("juno.sound.params.guidanceScaleHelp")} placement="top">
          <IconButton size="small">
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Slider
        value={clampedValue}
        onChange={(_, value) => setGuidanceScale(value as number)}
        min={guidanceScaleRange.min}
        max={guidanceScaleRange.max}
        step={guidanceScaleRange.step}
        valueLabelDisplay="auto"
        aria-labelledby="guidance-scale-slider"
        sx={{ width: "100%" }}
        marks={getMarks()}
      />
    </ColumnBox>
  );
};

export default GuidanceScaleControl;
